import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Plus, Search } from 'lucide-react';
import { llmGatewayApi } from '../../llm-gateway/llmGatewayApi';
import type { LlmGateway } from '../../llm-gateway/llmGatewayTypes';
import { clientTypeLabel, displayMcpToolName } from '../governedMcpConstants';
import ConfirmDialog from '../components/ConfirmDialog';
import LlmQuotaAssignDrawer from '../drawers/LlmQuotaAssignDrawer';
import QuotaAssignDrawer from '../drawers/QuotaAssignDrawer';
import QuotaEditDialog from '../drawers/QuotaEditDialog';
import QuotaScopeDrawer from '../drawers/QuotaScopeDrawer';
import QuotaUserDrawer from '../drawers/QuotaUserDrawer';
import EmptyState from '../components/EmptyState';
import { ClientStatusBadge } from '../components/StatusBadges';
import { DangerIconButton, GhostIconButton, TablePager, fieldClass, ghostBtn, initials, pageSlice, primaryBtn, selectField, tableHeadClass, tdClass, thClass, trClass } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type {
  ClientStatus,
  ClientType,
  McpCapabilityKind,
  PublishedCapability,
  QuotaAssignmentStatus,
  QuotaKind,
  QuotaListingTab,
  QuotaScope,
  QuotaUser,
  QuotaUserStatus,
} from '../governedMcpTypes';
import {
  buildAssignedQuotaUsers,
  buildEffectiveQuotaUsers,
  clientLabel,
  currentQuotaMonth,
  departmentMemberIds,
  filterQuotaUsers,
  findQuotaAssignment,
  findStoredQuotaAssignment,
  formatQuotaMonth,
  inferQuotaResourceType,
  isQuotaMonth,
  isQuotaPeriod,
  listQuotaFilterMonths,
  formatQuotaLimit,
  formatTokens,
  remainingTokens,
  quotaStatus,
  sourceLabel,
  tokensUsedInMonth,
  utilizationPercent,
} from '../quotaUsage';

type UtilFilter = 'all' | QuotaUserStatus;

const QUOTA_KIND_TABS: { id: QuotaKind; label: string; purpose: string }[] = [
  { id: 'tool', label: 'Tools', purpose: 'Control tool execution and external API usage.' },
  { id: 'llm', label: 'AI Gateway', purpose: 'Control model inference through the gateway.' },
  { id: 'client', label: 'Clients', purpose: 'Control consumption by applications, copilots, or clients.' },
];

const SCOPE_FILTERS: { id: Exclude<QuotaListingTab, 'client'>; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'user', label: 'User' },
  { id: 'role', label: 'Role' },
  { id: 'department', label: 'Department' },
];

const SCOPE_KIND_LABEL: Record<QuotaScope, string> = {
  all: 'All',
  user: 'User',
  role: 'Role',
  department: 'Department',
};

function capabilityOf(item: PublishedCapability): McpCapabilityKind {
  if (item.mcpCapability) return item.mcpCapability;
  const hasTool = item.mcpRepresentations?.includes('tool');
  const hasResource = item.mcpRepresentations?.includes('resource');
  if (hasResource && !hasTool) return 'resource';
  return 'tool';
}

function statusLabel(status: QuotaUserStatus): string {
  if (status === 'exceeded') return 'Over limit';
  if (status === 'warning') return 'High';
  return 'On track';
}

function barTone(status: QuotaUserStatus, isLight: boolean): string {
  if (status === 'exceeded') return isLight ? 'bg-rose-500' : 'bg-rose-400';
  if (status === 'warning') return isLight ? 'bg-amber-500' : 'bg-amber-400';
  return isLight ? 'bg-indigo-500' : 'bg-indigo-400';
}

function pillTone(status: QuotaUserStatus, isLight: boolean): string {
  if (status === 'exceeded') return isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/15 text-rose-300';
  if (status === 'warning') return isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function assignmentStatusTone(status: QuotaAssignmentStatus, isLight: boolean): string {
  if (status === 'inactive') return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function recordStatusTone(status: string, isLight: boolean): string {
  if (status === 'active' || status === 'published') return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
  if (status === 'disabled' || status === 'revoked') return isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/15 text-rose-300';
  return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
}

function sourceTone(source: string, isLight: boolean): string {
  if (source === 'User') return isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300';
  if (source === 'Role') return isLight ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/15 text-sky-300';
  if (source === 'Department') return isLight ? 'bg-violet-50 text-violet-700' : 'bg-violet-500/15 text-violet-300';
  return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
}

type ScopeRow = {
  scope: QuotaScope;
  scopeId: string;
  name: string;
  clientId: string;
  month: string;
  assignmentId: string | null;
  assignedLimit: number | null;
  status: QuotaAssignmentStatus | null;
  users: number;
  used: number;
  remaining: number;
  tokenLimit: number;
};

type DeleteTarget = {
  id: string;
  name: string;
  month: string;
};

type ClientRow = {
  id: string;
  name: string;
  clientType: ClientType;
  registrationId: string;
  status: ClientStatus;
  assignedLimit: number;
  used: number;
  remaining: number;
  tokenLimit: number;
};

type CombinedRow = {
  kind: QuotaScope;
  id: string;
  name: string;
  clientId: string;
  month: string;
  assignmentId: string | null;
  assignedLimit: number | null;
  status: QuotaAssignmentStatus | null;
  used: number;
  remaining: number;
  tokenLimit: number;
  user?: QuotaUser;
  scope?: ScopeRow;
};

function listingMonthValue(filter: string): string {
  return isQuotaPeriod(filter) ? filter : currentQuotaMonth();
}

export default function QuotasSpendingsTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, quotaDirectory, deleteQuotaAssignment } = useGovernedMcp();
  const [quotaKind, setQuotaKind] = useState<QuotaKind>('tool');
  const [listing, setListing] = useState<QuotaListingTab>('all');
  const [query, setQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(currentQuotaMonth());
  const [utilFilter, setUtilFilter] = useState<UtilFilter>('all');
  const [page, setPage] = useState(1);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<QuotaUser | null>(null);
  const [selectedScope, setSelectedScope] = useState<{
    scope: QuotaScope;
    scopeId: string;
    name: string;
    clientId: string;
    month: string;
  } | null>(null);
  const [gateways, setGateways] = useState<LlmGateway[]>([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [gatewaysError, setGatewaysError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [editingId, setEditingId] = useState('');

  const selectedKind = QUOTA_KIND_TABS.find((item) => item.id === quotaKind) ?? QUOTA_KIND_TABS[0];
  const clients = state.clients;

  useEffect(() => {
    if (!clients.length) return;
    if (!clientFilter || !clients.some((client) => client.id === clientFilter)) {
      setClientFilter(clients[0].id);
    }
  }, [clientFilter, clients]);

  useEffect(() => {
    let cancelled = false;
    setGatewaysLoading(true);
    setGatewaysError(null);
    logGovernedMcp('app', 'quota_listing_gateways_load');
    llmGatewayApi
      .listGateways()
      .then((items) => {
        if (cancelled) return;
        setGateways(items);
        logGovernedMcp('app', 'quota_listing_gateways_loaded', { count: items.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load LLM gateways.';
        setGatewaysError(message);
        logGovernedMcp('error', 'quota_listing_gateways_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setGatewaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const registeredClientIds = useMemo(() => new Set(clients.map((item) => item.id)), [clients]);
  const gatewayIds = useMemo(() => new Set(gateways.map((item) => item.id)), [gateways]);
  const monthOptions = useMemo(() => listQuotaFilterMonths(state.quotaAssignments), [state.quotaAssignments]);
  const listingMonth = listingMonthValue(monthFilter);
  const monthUsers = useMemo(
    () =>
      buildEffectiveQuotaUsers(state.apiKeys, state.quotaAssignments, state.quotaUsages, quotaDirectory, listingMonth),
    [listingMonth, quotaDirectory, state.apiKeys, state.quotaAssignments, state.quotaUsages]
  );
  const users = useMemo(
    () =>
      buildAssignedQuotaUsers(
        state.quotaAssignments,
        state.quotaUsages,
        quotaDirectory,
        monthFilter,
        registeredClientIds,
        'client',
        gatewayIds
      ),
    [gatewayIds, monthFilter, quotaDirectory, registeredClientIds, state.quotaAssignments, state.quotaUsages]
  );

  const clientRows = useMemo<ClientRow[]>(
    () =>
      clients.map((client) => {
        const members = filterQuotaUsers(monthUsers, client.id);
        const tokenLimit = members.reduce((sum, user) => sum + user.tokenLimit, 0);
        const used = members.reduce((sum, user) => sum + user.tokensUsed, 0);
        return {
          id: client.id,
          name: client.name,
          clientType: client.clientType,
          registrationId: client.registrationId,
          status: client.status,
          assignedLimit: tokenLimit,
          used,
          remaining: Math.max(0, tokenLimit - used),
          tokenLimit,
        };
      }),
    [clients, monthUsers]
  );

  const scopeName = (scope: QuotaScope, scopeId: string): string => {
    if (scope === 'all') return 'All';
    if (scope === 'department') return quotaDirectory.departments.find((item) => item.id === scopeId)?.name || scopeId;
    if (scope === 'role') return quotaDirectory.roles.find((item) => item.id === scopeId)?.name || scopeId;
    return quotaDirectory.users.find((item) => item.id === scopeId)?.name || scopeId;
  };

  const scopeRows = useMemo(() => {
    const rows: ScopeRow[] = [];
    const scopes: QuotaScope[] =
      listing === 'all' ? ['role', 'department'] : listing === 'role' || listing === 'department' ? [listing] : [];
    if (!scopes.length) return rows;

    const pushRow = (assignment: NonNullable<ReturnType<typeof findQuotaAssignment>>, scope: QuotaScope, scopeId: string, name: string, clientId: string, month: string) => {
      const memberIds = new Set(
        scope === 'department'
          ? departmentMemberIds(quotaDirectory, scopeId)
          : quotaDirectory.users.filter((user) => user.roleId === scopeId).map((user) => user.id)
      );
      const used =
        monthFilter === 'all'
          ? state.quotaUsages
              .filter((usage) => memberIds.has(usage.userId) && usage.clientId === clientId)
              .reduce((sum, usage) => sum + tokensUsedInMonth(usage, month), 0)
          : monthUsers
              .filter((user) => user.userId && memberIds.has(user.userId) && user.clientId === clientId)
              .reduce((sum, user) => sum + user.tokensUsed, 0);
      const perUserLimit = assignment.status === 'active' ? assignment.tokenLimit : 0;
      const tokenLimit = perUserLimit * Math.max(memberIds.size, 1);
      rows.push({
        scope,
        scopeId,
        name,
        clientId,
        month,
        assignmentId: assignment.id,
        assignedLimit: assignment.tokenLimit,
        status: assignment.status,
        users: memberIds.size,
        used,
        remaining: Math.max(0, tokenLimit - used),
        tokenLimit,
      });
    };

    if (monthFilter === 'all') {
      const seen = new Set<string>();
      for (const item of state.quotaAssignments) {
        if (!scopes.includes(item.scope)) continue;
        if (!isQuotaMonth(item.month) || item.tokenLimit <= 0) continue;
        if (!registeredClientIds.has(item.clientId)) continue;
        if (inferQuotaResourceType(item, registeredClientIds, gatewayIds) !== 'client') continue;
        const applied = findStoredQuotaAssignment(
          state.quotaAssignments,
          item.scope,
          item.scopeId,
          item.clientId,
          item.month,
          item.clientId
        );
        if (!applied || applied.id !== item.id) continue;
        const key = `${item.scope}::${item.scopeId}::${item.clientId}::${item.month}`;
        if (seen.has(key)) continue;
        seen.add(key);
        pushRow(applied, item.scope, item.scopeId, scopeName(item.scope, item.scopeId), item.clientId, item.month);
      }
      return rows;
    }

    for (const scope of scopes) {
      const items =
        scope === 'department'
          ? quotaDirectory.departments.map((item) => ({ id: item.id, name: item.name }))
          : quotaDirectory.roles.map((item) => ({ id: item.id, name: item.name }));
      for (const item of items) {
        for (const client of clients) {
          const assignment = findQuotaAssignment(state.quotaAssignments, scope, item.id, client.id, monthFilter, client.id);
          if (!assignment || assignment.tokenLimit <= 0) continue;
          pushRow(assignment, scope, item.id, item.name, client.id, monthFilter);
        }
      }
    }
    return rows;
  }, [clients, gatewayIds, listing, monthFilter, monthUsers, quotaDirectory, registeredClientIds, state.quotaAssignments, state.quotaUsages]);

  const publishedTools = useMemo(
    () => state.published.filter((item) => capabilityOf(item) === 'tool'),
    [state.published]
  );
  const publishedToolIds = useMemo(
    () => new Set(publishedTools.flatMap((item) => [item.id, item.mcpToolName].filter(Boolean))),
    [publishedTools]
  );
  const visibleTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.quotaAssignments
      .filter((item) => {
        if (listing !== 'all' && listing !== 'client' && item.scope !== listing) return false;
        if (inferQuotaResourceType(item, registeredClientIds, gatewayIds) !== 'mcp_tool') return false;
        if (!registeredClientIds.has(item.clientId)) return false;
        const toolKey = item.resourceId || item.clientId;
        if (!publishedToolIds.has(toolKey)) return false;
        const lookup = monthFilter === 'all' ? findStoredQuotaAssignment : findQuotaAssignment;
        const lookupMonth = monthFilter === 'all' ? item.month : monthFilter;
        return (
          lookup(
            state.quotaAssignments,
            item.scope,
            item.scopeId,
            item.clientId,
            lookupMonth,
            toolKey,
            publishedTools
          )?.id === item.id
        );
      })
      .filter((item) => {
        const tool = publishedTools.find((row) => row.id === item.resourceId || row.mcpToolName === item.resourceId || row.id === item.clientId);
        const haystack = `${item.name} ${tool?.mcpToolName || ''} ${tool?.sourceName || ''} ${scopeName(item.scope, item.scopeId)}`.toLowerCase();
        if (utilFilter !== 'all') {
          const status = quotaStatus({ tokenLimit: item.tokenLimit, tokensUsed: 0 });
          if (status !== utilFilter) return false;
        }
        return !q || haystack.includes(q);
      })
      .map((item) => {
        const tool = publishedTools.find((row) => row.id === item.resourceId || row.mcpToolName === item.resourceId || row.id === item.clientId);
        return {
          id: item.id,
          name: displayMcpToolName(item.name || tool?.mcpToolName || tool?.sourceName || item.resourceId || item.clientId),
          source: tool?.sourceName || scopeName(item.scope, item.scopeId),
          scope: item.scope,
          scopeLabel: scopeName(item.scope, item.scopeId),
          clientId: item.clientId,
          month: item.month,
          status: item.status,
          tokenLimit: item.tokenLimit,
          renewsMonthly: item.renewsMonthly,
        };
      });
  }, [
    gatewayIds,
    listing,
    monthFilter,
    publishedToolIds,
    publishedTools,
    query,
    quotaDirectory.departments,
    quotaDirectory.roles,
    quotaDirectory.users,
    registeredClientIds,
    state.quotaAssignments,
    utilFilter,
  ]);

  const visibleGateways = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.quotaAssignments
      .filter((item) => {
        if (listing !== 'all' && listing !== 'client' && item.scope !== listing) return false;
        if (inferQuotaResourceType(item, registeredClientIds, gatewayIds) !== 'llm_gateway') return false;
        const lookup = monthFilter === 'all' ? findStoredQuotaAssignment : findQuotaAssignment;
        const lookupMonth = monthFilter === 'all' ? item.month : monthFilter;
        return lookup(
          state.quotaAssignments,
          item.scope,
          item.scopeId,
          item.clientId,
          lookupMonth,
          item.resourceId || item.clientId,
          gateways
        )?.id === item.id;
      })
      .filter((item) => {
        const gateway = gateways.find((row) => row.id === (item.resourceId || item.clientId));
        const haystack = `${item.name} ${gateway?.name || ''} ${gateway?.publicAlias || ''} ${scopeName(item.scope, item.scopeId)}`.toLowerCase();
        if (utilFilter !== 'all') {
          const status = quotaStatus({ tokenLimit: item.tokenLimit, tokensUsed: 0 });
          if (status !== utilFilter) return false;
        }
        return !q || haystack.includes(q);
      })
      .map((item) => {
        const gateway = gateways.find((row) => row.id === (item.resourceId || item.clientId));
        return {
          id: item.id,
          name: item.name || gateway?.name || item.resourceId || item.clientId,
          publicAlias: gateway?.publicAlias || '',
          month: item.month,
          status: item.status,
          tokenLimit: item.tokenLimit,
          scope: item.scope,
          scopeId: item.scopeId,
          scopeLabel: scopeName(item.scope, item.scopeId),
          renewsMonthly: item.renewsMonthly,
        };
      });
  }, [gateways, gatewayIds, listing, monthFilter, query, quotaDirectory.departments, quotaDirectory.roles, quotaDirectory.users, state.quotaAssignments, utilFilter]);

  const totals = useMemo(() => {
    if (listing === 'client') {
      return clientRows.reduce(
        (acc, row) => {
          acc.limit += row.tokenLimit;
          acc.used += row.used;
          acc.remaining += row.remaining;
          return acc;
        },
        { limit: 0, used: 0, remaining: 0 }
      );
    }
    if (listing === 'user' || listing === 'all') {
      const fromUsers = users.reduce(
        (acc, user) => {
          acc.limit += user.tokenLimit;
          acc.used += user.tokensUsed;
          acc.remaining += remainingTokens(user);
          return acc;
        },
        { limit: 0, used: 0, remaining: 0 }
      );
      if (listing === 'user') return fromUsers;
      return scopeRows.reduce(
        (acc, row) => {
          acc.limit += row.tokenLimit;
          acc.used += row.used;
          acc.remaining += row.remaining;
          return acc;
        },
        fromUsers
      );
    }
    return scopeRows.reduce(
      (acc, row) => {
        acc.limit += row.tokenLimit;
        acc.used += row.used;
        acc.remaining += row.remaining;
        return acc;
      },
      { limit: 0, used: 0, remaining: 0 }
    );
  }, [clientRows, listing, scopeRows, users]);

  const visibleScopeRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scopeRows.filter((row) => {
      const status = quotaStatus({ tokenLimit: row.tokenLimit, tokensUsed: row.used });
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${row.name} ${clientLabel(row.clientId, clients)}`.toLowerCase().includes(q);
    });
  }, [clients, query, scopeRows, utilFilter]);

  const visibleUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((user) => {
      const status = quotaStatus(user);
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${user.name} ${user.email} ${user.role} ${clientLabel(user.clientId, clients)}`.toLowerCase().includes(q);
    });
  }, [clients, query, users, utilFilter]);

  const combinedRows = useMemo<CombinedRow[]>(() => {
    const fromUsers: CombinedRow[] = visibleUsers.map((user) => {
      const rowMonth = user.month || listingMonth;
      const assigned =
        monthFilter === 'all'
          ? findStoredQuotaAssignment(state.quotaAssignments, 'user', user.userId || '', user.clientId, rowMonth, user.clientId)
          : findQuotaAssignment(state.quotaAssignments, 'user', user.userId || '', user.clientId, monthFilter, user.clientId);
      return {
        kind: 'user',
        id: `${user.id}-${rowMonth}`,
        name: user.name,
        clientId: user.clientId,
        month: rowMonth,
        assignmentId: assigned?.id ?? null,
        assignedLimit: assigned?.tokenLimit ?? user.tokenLimit,
        status: assigned?.status ?? null,
        used: user.tokensUsed,
        remaining: remainingTokens(user),
        tokenLimit: user.tokenLimit,
        user,
      };
    });
    const fromScopes: CombinedRow[] = visibleScopeRows.map((row) => ({
      kind: row.scope,
      id: `${row.scope}-${row.scopeId}-${row.clientId}-${row.month}`,
      name: row.name,
      clientId: row.clientId,
      month: row.month,
      assignmentId: row.assignmentId,
      assignedLimit: row.assignedLimit,
      status: row.status,
      used: row.used,
      remaining: row.remaining,
      tokenLimit: row.tokenLimit,
      scope: row,
    }));
    return [...fromUsers, ...fromScopes];
  }, [listingMonth, monthFilter, state.quotaAssignments, visibleScopeRows, visibleUsers]);

  useEffect(() => {
    const stored = state.quotaAssignments.filter((item) => item.scope === 'user').length;
    logGovernedMcp('app', 'quota_listing_user_visibility', {
      month: monthFilter,
      listing,
      quotaKind,
      resourceType: 'client',
      stored,
      assignedRows: users.length,
      visible: visibleUsers.length,
    });
  }, [listing, monthFilter, quotaKind, state.quotaAssignments, users.length, visibleUsers.length]);

  const visibleClientRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clientRows.filter((row) => {
      const status = quotaStatus({ tokenLimit: row.tokenLimit, tokensUsed: row.used });
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${row.name} ${row.registrationId} ${clientTypeLabel(row.clientType)}`.toLowerCase().includes(q);
    });
  }, [clientRows, query, utilFilter]);

  useEffect(() => {
    setPage(1);
  }, [quotaKind, listing, query, clientFilter, monthFilter, utilFilter]);

  const listingRows =
    quotaKind === 'tool'
      ? visibleTools
      : quotaKind === 'llm'
        ? visibleGateways
        : listing === 'client'
          ? visibleClientRows
          : listing === 'all'
            ? combinedRows
            : listing === 'user'
              ? visibleUsers
              : visibleScopeRows;
  const pagedUsers = pageSlice(visibleUsers, page);
  const pagedScopeRows = pageSlice(visibleScopeRows, page);
  const pagedCombinedRows = pageSlice(combinedRows, page);
  const pagedClientRows = pageSlice(visibleClientRows, page);
  const pagedTools = pageSlice(visibleTools, page);
  const pagedGateways = pageSlice(visibleGateways, page);

  const assignedLimitLabel = monthFilter === 'all' ? 'Assigned limit' : `Assigned limit · ${formatQuotaMonth(monthFilter)}`;
  const summaryCards =
    quotaKind === 'tool'
      ? [
          { label: 'Assigned tools', value: String(visibleTools.length) },
          { label: assignedLimitLabel, value: visibleTools.reduce((sum, row) => sum + row.tokenLimit, 0).toLocaleString() },
          { label: 'Active', value: String(visibleTools.filter((row) => row.status === 'active').length) },
        ]
      : quotaKind === 'llm'
        ? [
            { label: 'Assigned gateways', value: String(visibleGateways.length) },
            {
              label: assignedLimitLabel,
              value: formatQuotaLimit(visibleGateways.some((row) => row.tokenLimit <= 0) ? 0 : visibleGateways.reduce((sum, row) => sum + row.tokenLimit, 0)),
            },
            { label: 'Active', value: String(visibleGateways.filter((row) => row.status === 'active').length) },
          ]
        : [
            { label: assignedLimitLabel, value: formatTokens(totals.limit) },
            { label: 'Current spend', value: formatTokens(totals.used) },
            { label: 'Remaining', value: formatTokens(totals.remaining) },
          ];

  const empty =
    quotaKind === 'tool'
      ? { title: 'No tool quotas', description: 'Assign a max tool-call quota to a role, department, or user to see it here.' }
      : quotaKind === 'llm'
        ? { title: 'No AI gateway quotas', description: 'Assign a monthly token limit to a role, department, or user to see it here.' }
        : listing === 'client'
          ? { title: 'No clients', description: 'Register an AI client to review consumption and assign a monthly quota.' }
          : listing === 'all'
            ? { title: 'No assignments', description: 'Assign a monthly quota to a user, role, or department to see it here.' }
            : listing === 'user'
              ? { title: 'No assigned users', description: 'Assign a monthly quota to a user to see it here.' }
              : { title: 'No assignments', description: 'No departments or roles have a quota greater than 0 for this client and month.' };

  const noun =
    quotaKind === 'tool'
      ? 'tool'
      : quotaKind === 'llm'
        ? 'gateway'
        : listing === 'client'
          ? 'client'
          : listing === 'all'
            ? 'assignment'
            : listing === 'user'
              ? 'user'
              : listing;

  const requestEdit = (id: string) => {
    const current = state.quotaAssignments.find((item) => item.id === id);
    if (!current) {
      logGovernedMcp('warning', 'quota_listing_edit_blocked', { id, reason: 'missing' });
      onToast('error', 'That quota is no longer assigned.');
      return;
    }
    setEditingId(current.id);
    logGovernedMcp('app', 'quota_listing_edit_opened', {
      id: current.id,
      scope: current.scope,
      scopeId: current.scopeId,
      clientId: current.clientId,
      resourceId: current.resourceId,
      month: current.month,
      resourceType: current.resourceType,
    });
  };

  const requestDelete = (target: DeleteTarget) => {
    setDeleteTarget(target);
    logGovernedMcp('app', 'quota_listing_delete_opened', { id: target.id, month: target.month });
  };

  const editing = editingId ? state.quotaAssignments.find((item) => item.id === editingId) ?? null : null;

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const result = deleteQuotaAssignment(deleteTarget.id);
    if (!result.ok) {
      logGovernedMcp('warning', 'quota_listing_delete_blocked', { id: deleteTarget.id, reason: result.reason });
      onToast('error', result.reason);
      setDeleteTarget(null);
      return;
    }
    logGovernedMcp('app', 'quota_listing_delete_confirmed', {
      id: deleteTarget.id,
      name: deleteTarget.name,
      month: deleteTarget.month,
    });
    onToast('success', `Quota removed for ${deleteTarget.name} · ${formatQuotaMonth(deleteTarget.month)}.`);
    setDeleteTarget(null);
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {summaryCards.map((item) => (
          <div
            key={item.label}
            className={`rounded-xl border px-4 py-3 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}
          >
            <p className={`text-[11px] uppercase tracking-wide ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.label}</p>
            <p className={`mt-1 text-[15px] font-semibold tabular-nums ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input
            className={`${fieldClass(isLight)} pl-9`}
            placeholder={
              quotaKind === 'tool'
                ? 'Search published tools…'
                : quotaKind === 'llm'
                  ? 'Search AI gateways…'
                  : listing === 'all'
                    ? 'Search all assignments…'
                    : listing === 'user'
                      ? 'Search assigned users…'
                      : `Search ${listing}s…`
            }
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <select
          className={selectField(isLight)}
          value={quotaKind}
          aria-label="Quota type"
          onChange={(event) => {
            const next = event.target.value as QuotaKind;
            setQuotaKind(next);
            setPage(1);
            setQuery('');
            setSelectedScope(null);
            setSelectedUser(null);
            logGovernedMcp('app', 'quota_listing_kind', { quotaKind: next });
          }}
        >
          {QUOTA_KIND_TABS.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
        <select
          className={selectField(isLight)}
          value={listing === 'client' ? 'all' : listing}
          aria-label="Role, department, or user"
          onChange={(event) => {
            const next = event.target.value as Exclude<QuotaListingTab, 'client'>;
            setListing(next);
            setPage(1);
            setSelectedScope(null);
            setSelectedUser(null);
            logGovernedMcp('app', 'quota_listing_scope', { listing: next, quotaKind });
          }}
        >
          {SCOPE_FILTERS.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
        <select
          className={selectField(isLight)}
          value={monthFilter}
          aria-label="Quota month"
          onChange={(event) => {
            const next = event.target.value;
            logGovernedMcp('app', 'quota_month_filter', { month: next });
            setMonthFilter(next);
          }}
        >
          <option value="all">All</option>
          {monthOptions.map((month) => (
            <option key={month} value={month}>
              {formatQuotaMonth(month)}
            </option>
          ))}
        </select>
        <select className={selectField(isLight)} value={utilFilter} onChange={(event) => setUtilFilter(event.target.value as UtilFilter)}>
          <option value="all">All utilization</option>
          <option value="ok">On track</option>
          <option value="warning">High</option>
          <option value="exceeded">Over limit</option>
        </select>
        <button type="button" className={`${primaryBtn()} rounded-full shrink-0`} onClick={() => setAssignOpen(true)}>
          <Plus size={14} /> Assign quota
        </button>
      </div>
      <p className={`text-sm mb-4 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{selectedKind.purpose}</p>

      {listingRows.length === 0 ? (
        <EmptyState isLight={isLight} title={empty.title} description={empty.description} />
      ) : (
        <div
          className={`overflow-auto min-h-[280px] h-[calc(100vh-22rem)] rounded-2xl border shadow-sm ${
            isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'
          }`}
        >
          <table className="w-full min-w-[1100px] text-sm">
            <thead className={tableHeadClass(isLight)}>
              {quotaKind === 'tool' ? (
                <tr>
                  <th className={thClass()}>Tool</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>
                    {listing === 'all' ? 'Assigned to' : listing === 'user' ? 'User' : listing === 'role' ? 'Role' : 'Department'}
                  </th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Max tool calls</th>
                  <th className={thClass()}> </th>
                </tr>
              ) : quotaKind === 'llm' ? (
                <tr>
                  <th className={thClass()}>AI gateway</th>
                  <th className={thClass()}>
                    {listing === 'all' ? 'Assigned to' : listing === 'user' ? 'User' : listing === 'role' ? 'Role' : 'Department'}
                  </th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Monthly token limit</th>
                  <th className={thClass()}> </th>
                </tr>
              ) : listing === 'client' ? (
                <tr>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Type</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              ) : listing === 'all' ? (
                <tr>
                  <th className={thClass()}> </th>
                  <th className={thClass()}>Type</th>
                  <th className={thClass()}>Name</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              ) : listing === 'user' ? (
                <tr>
                  <th className={thClass()}> </th>
                  <th className={thClass()}>User</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Effective</th>
                  <th className={thClass()}>Source</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              ) : (
                <tr>
                  <th className={thClass()}> </th>
                  <th className={thClass()}>{listing === 'department' ? 'Department' : 'Role'}</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Users</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              )}
            </thead>
            <tbody>
              {quotaKind === 'tool'
                ? pagedTools.map((item) => (
                    <tr key={item.id} className={trClass(isLight)}>
                      <td className={tdClass()}>
                        <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.name}</p>
                        {item.source ? (
                          <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.source}</p>
                        ) : null}
                      </td>
                      <td className={tdClass()}>{clientLabel(item.clientId, clients)}</td>
                      <td className={tdClass()}>
                        <p>{item.scopeLabel}</p>
                        {listing === 'all' ? (
                          <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                            {SCOPE_KIND_LABEL[item.scope]}
                          </p>
                        ) : null}
                      </td>
                      <td className={tdClass()}>{formatQuotaMonth(monthFilter === 'all' ? item.month : monthFilter)}</td>
                      <td className={tdClass()}>
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(item.status, isLight)}`}>
                            {item.status === 'inactive' ? 'Inactive' : 'Active'}
                          </span>
                          {item.renewsMonthly ? (
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${isLight ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/15 text-sky-300'}`}>
                              Renews monthly
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className={`${tdClass()} tabular-nums`}>{item.tokenLimit.toLocaleString()}</td>
                      <td className={`${tdClass()} text-right`}>
                        <div className="inline-flex items-center justify-end gap-0.5">
                          <GhostIconButton isLight={isLight} label="Edit quota" onClick={() => requestEdit(item.id)} />
                          <DangerIconButton
                            isLight={isLight}
                            label="Delete quota"
                            onClick={() =>
                              requestDelete({
                                id: item.id,
                                name: item.name,
                                month: item.month || listingMonth,
                              })
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                : quotaKind === 'llm'
                  ? pagedGateways.map((item) => (
                      <tr key={item.id} className={trClass(isLight)}>
                        <td className={tdClass()}>
                          <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.name}</p>
                          {item.publicAlias ? (
                            <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.publicAlias}</p>
                          ) : null}
                        </td>
                        <td className={tdClass()}>
                          <p>{item.scopeLabel}</p>
                          {listing === 'all' ? (
                            <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                              {SCOPE_KIND_LABEL[item.scope]}
                            </p>
                          ) : null}
                        </td>
                        <td className={tdClass()}>{formatQuotaMonth(monthFilter === 'all' ? item.month : monthFilter)}</td>
                        <td className={tdClass()}>
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(item.status, isLight)}`}>
                              {item.status === 'inactive' ? 'Inactive' : 'Active'}
                            </span>
                            {item.renewsMonthly ? (
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${isLight ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/15 text-sky-300'}`}>
                                Renews monthly
                              </span>
                            ) : null}
                          </span>
                        </td>
                        <td className={`${tdClass()} tabular-nums`}>{formatQuotaLimit(item.tokenLimit)}</td>
                        <td className={`${tdClass()} text-right`}>
                          <div className="inline-flex items-center justify-end gap-0.5">
                            <GhostIconButton isLight={isLight} label="Edit quota" onClick={() => requestEdit(item.id)} />
                            <DangerIconButton
                              isLight={isLight}
                              label="Delete quota"
                              onClick={() =>
                                requestDelete({
                                  id: item.id,
                                  name: item.name,
                                  month: item.month || listingMonth,
                                })
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  : listing === 'client'
                    ? pagedClientRows.map((row) => (
                        <tr key={row.id} className={trClass(isLight)}>
                          <td className={tdClass()}>
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className={`h-9 w-9 rounded-xl text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                                  isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                                }`}
                              >
                                {initials(row.name)}
                              </span>
                              <div className="min-w-0">
                                <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.name}</p>
                                <p className={`text-xs truncate ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{row.registrationId}</p>
                              </div>
                            </div>
                          </td>
                          <td className={tdClass()}>{clientTypeLabel(row.clientType)}</td>
                          <td className={tdClass()}>
                            <ClientStatusBadge status={row.status} />
                          </td>
                          <td className={tdClass()}>{formatQuotaMonth(listingMonth)}</td>
                          <td className={`${tdClass()} tabular-nums`}>
                            {row.assignedLimit > 0 ? formatTokens(row.assignedLimit) : <span className={isLight ? 'text-neutral-400' : 'text-neutral-500'}>—</span>}
                          </td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.used)}</td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.remaining)}</td>
                          <td className={tdClass()}>
                            <Utilization user={{ tokenLimit: row.tokenLimit, tokensUsed: row.used }} isLight={isLight} />
                          </td>
                        </tr>
                      ))
                    : listing === 'all'
                    ? pagedCombinedRows.map((row) => (
                        <tr key={row.id} className={trClass(isLight)}>
                          <td className={`${tdClass()} text-left whitespace-nowrap`}>
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                className={`${ghostBtn(isLight)} h-8 px-2.5`}
                                onClick={() => {
                                  if (row.user) {
                                    setSelectedScope(null);
                                    setSelectedUser(row.user);
                                    return;
                                  }
                                  if (row.scope) {
                                    setSelectedUser(null);
                                    setSelectedScope({
                                      scope: row.scope.scope,
                                      scopeId: row.scope.scopeId,
                                      name: row.scope.name,
                                      clientId: row.scope.clientId || clientFilter,
                                      month: row.month || listingMonth,
                                    });
                                  }
                                }}
                              >
                                <BarChart3 size={13} />
                                Details
                              </button>
                              {row.assignmentId ? (
                                <GhostIconButton
                                  isLight={isLight}
                                  label="Edit quota"
                                  onClick={() => {
                                    if (row.assignmentId) requestEdit(row.assignmentId);
                                  }}
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className={tdClass()}>
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${sourceTone(SCOPE_KIND_LABEL[row.kind], isLight)}`}>
                              {SCOPE_KIND_LABEL[row.kind]}
                            </span>
                          </td>
                          <td className={tdClass()}>
                            <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.name}</p>
                          </td>
                          <td className={tdClass()}>{row.clientId ? clientLabel(row.clientId, clients) : 'Select a client'}</td>
                          <td className={tdClass()}>{formatQuotaMonth(monthFilter === 'all' ? row.month : monthFilter)}</td>
                          <td className={`${tdClass()} tabular-nums`}>
                            {row.assignedLimit == null ? '—' : formatTokens(row.assignedLimit)}
                          </td>
                          <td className={tdClass()}>
                            {row.status ? (
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(row.status, isLight)}`}>
                                {row.status === 'inactive' ? 'Inactive' : 'Active'}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.used)}</td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.remaining)}</td>
                          <td className={tdClass()}>
                            <Utilization user={{ tokenLimit: row.tokenLimit, tokensUsed: row.used }} isLight={isLight} />
                          </td>
                        </tr>
                      ))
                    : listing === 'user'
                    ? pagedUsers.map((user) => {
                        const rowMonth = user.month || listingMonth;
                        const assigned =
                          monthFilter === 'all'
                            ? findStoredQuotaAssignment(
                                state.quotaAssignments,
                                'user',
                                user.userId || '',
                                user.clientId,
                                rowMonth,
                                user.clientId
                              )
                            : findQuotaAssignment(
                                state.quotaAssignments,
                                'user',
                                user.userId || '',
                                user.clientId,
                                monthFilter,
                                user.clientId
                              );
                        return (
                          <tr key={user.id} className={trClass(isLight)}>
                            <td className={`${tdClass()} text-left whitespace-nowrap`}>
                              <div className="inline-flex items-center gap-1">
                                <button
                                  type="button"
                                  className={`${ghostBtn(isLight)} h-8 px-2.5`}
                                  onClick={() => {
                                    setSelectedScope(null);
                                    setSelectedUser(user);
                                  }}
                                >
                                  <BarChart3 size={13} />
                                  Details
                                </button>
                                {assigned ? (
                                  <GhostIconButton isLight={isLight} label="Edit quota" onClick={() => requestEdit(assigned.id)} />
                                ) : null}
                              </div>
                            </td>
                            <td className={tdClass()}>
                              <div className="flex items-center gap-3 min-w-0">
                                <span
                                  className={`h-9 w-9 rounded-xl text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                                    isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                                  }`}
                                >
                                  {initials(user.name)}
                                </span>
                                <div className="min-w-0">
                                  <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{user.name}</p>
                                  <p className={`text-xs truncate ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                    {user.email} · {user.role}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className={tdClass()}>{clientLabel(user.clientId, clients)}</td>
                            <td className={tdClass()}>{formatQuotaMonth(monthFilter === 'all' ? rowMonth : monthFilter)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{assigned ? formatTokens(assigned.tokenLimit) : '—'}</td>
                            <td className={tdClass()}>
                              {assigned ? (
                                <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(assigned.status, isLight)}`}>
                                  {assigned.status === 'inactive' ? 'Inactive' : 'Active'}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(user.tokenLimit)}</td>
                            <td className={tdClass()}>
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${sourceTone(sourceLabel(user.source), isLight)}`}>
                                {sourceLabel(user.source)}
                              </span>
                            </td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(user.tokensUsed)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(remainingTokens(user))}</td>
                            <td className={tdClass()}>
                              <Utilization user={user} isLight={isLight} />
                            </td>
                          </tr>
                        );
                      })
                    : pagedScopeRows.map((row) => {
                        const rowKey = `${row.scope}-${row.scopeId}-${row.clientId}-${row.month}`;
                        return (
                          <tr key={rowKey} className={trClass(isLight)}>
                            <td className={`${tdClass()} text-left whitespace-nowrap`}>
                              <div className="inline-flex items-center gap-1">
                                <button
                                  type="button"
                                  className={`${ghostBtn(isLight)} h-8 px-2.5`}
                                  onClick={() => {
                                    setSelectedUser(null);
                                    setSelectedScope({
                                      scope: row.scope,
                                      scopeId: row.scopeId,
                                      name: row.name,
                                      clientId: row.clientId || clientFilter,
                                      month: row.month || listingMonth,
                                    });
                                  }}
                                >
                                  <BarChart3 size={13} />
                                  Details
                                </button>
                                {row.assignmentId ? (
                                  <GhostIconButton
                                    isLight={isLight}
                                    label="Edit quota"
                                    onClick={() => {
                                      if (row.assignmentId) requestEdit(row.assignmentId);
                                    }}
                                  />
                                ) : null}
                              </div>
                            </td>
                            <td className={tdClass()}>
                              <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.name}</p>
                            </td>
                            <td className={tdClass()}>{row.clientId ? clientLabel(row.clientId, clients) : 'Select a client'}</td>
                            <td className={tdClass()}>{formatQuotaMonth(monthFilter === 'all' ? row.month : monthFilter)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{row.users}</td>
                            <td className={`${tdClass()} tabular-nums`}>
                              {row.assignedLimit == null ? '—' : formatTokens(row.assignedLimit)}
                            </td>
                            <td className={tdClass()}>
                              {row.status ? (
                                <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(row.status, isLight)}`}>
                                  {row.status === 'inactive' ? 'Inactive' : 'Active'}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.used)}</td>
                            <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.remaining)}</td>
                            <td className={tdClass()}>
                              <Utilization user={{ tokenLimit: row.tokenLimit, tokensUsed: row.used }} isLight={isLight} />
                            </td>
                          </tr>
                        );
                      })}
            </tbody>
          </table>
        </div>
      )}
      {listingRows.length > 0 ? <TablePager page={page} total={listingRows.length} isLight={isLight} onPage={setPage} noun={noun} /> : null}

      {quotaKind === 'llm' ? (
        <LlmQuotaAssignDrawer
          open={assignOpen}
          listing={listing}
          clientId={clientFilter}
          month={listingMonth}
          isLight={isLight}
          onClose={() => setAssignOpen(false)}
          onAssigned={({ listing: nextListing, month: nextMonth }) => {
            if (listing !== 'all' && (nextListing === 'user' || nextListing === 'role' || nextListing === 'department' || nextListing === 'all')) {
              setListing(nextListing);
            }
            if (monthFilter !== 'all' && isQuotaPeriod(nextMonth)) setMonthFilter(nextMonth);
            logGovernedMcp('app', 'llm_quota_listing_after_assign', {
              listing: listing === 'all' ? 'all' : nextListing,
              month: monthFilter === 'all' ? 'all' : nextMonth,
            });
          }}
          onToast={onToast}
        />
      ) : (
        <QuotaAssignDrawer
          open={assignOpen}
          listing={listing}
          quotaKind={quotaKind}
          clientId={clientFilter}
          month={listingMonth}
          isLight={isLight}
          onClose={() => setAssignOpen(false)}
          onAssigned={({ listing: nextListing, month: nextMonth, quotaKind: nextKind }) => {
            setQuotaKind(nextKind);
            if (listing !== 'all' && (nextListing === 'user' || nextListing === 'role' || nextListing === 'department')) {
              setListing(nextListing);
            }
            if (monthFilter !== 'all' && isQuotaMonth(nextMonth)) setMonthFilter(nextMonth);
            logGovernedMcp('app', 'quota_listing_after_assign', {
              listing: listing === 'all' ? 'all' : nextListing,
              month: monthFilter === 'all' ? 'all' : nextMonth,
              quotaKind: nextKind,
            });
          }}
          onToast={onToast}
        />
      )}
      <QuotaUserDrawer
        user={selectedUser}
        clientFilter={clientFilter}
        month={selectedUser?.month && isQuotaMonth(selectedUser.month) ? selectedUser.month : listingMonth}
        isLight={isLight}
        onClose={() => setSelectedUser(null)}
        onToast={onToast}
      />
      <QuotaScopeDrawer
        open={Boolean(selectedScope)}
        scope={selectedScope?.scope || 'department'}
        scopeId={selectedScope?.scopeId || ''}
        name={selectedScope?.name || ''}
        clientId={selectedScope?.clientId || clientFilter}
        month={selectedScope?.month && isQuotaMonth(selectedScope.month) ? selectedScope.month : listingMonth}
        isLight={isLight}
        onClose={() => setSelectedScope(null)}
        onToast={onToast}
        onOpenUser={(user) => {
          setSelectedScope(null);
          setSelectedUser(user);
        }}
      />
      <QuotaEditDialog
        assignment={editing}
        assignedTo={editing ? scopeName(editing.scope, editing.scopeId) : ''}
        clientName={editing ? clientLabel(editing.clientId, clients) : ''}
        isLight={isLight}
        onClose={() => setEditingId('')}
        onToast={onToast}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete quota?"
        description={
          deleteTarget
            ? `Remove the ${formatQuotaMonth(deleteTarget.month)} quota for ${deleteTarget.name}? Enforcement for this tenant stops immediately.`
            : 'Remove this quota assignment?'
        }
        confirmLabel="Delete quota"
        danger
        isLight={isLight}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function Utilization({
  user,
  isLight,
}: {
  user: Pick<QuotaUser, 'tokenLimit' | 'tokensUsed'>;
  isLight: boolean;
}) {
  const status = quotaStatus(user);
  const percent = utilizationPercent(user);
  return (
    <div className="min-w-[140px]">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${pillTone(status, isLight)}`}>{statusLabel(status)}</span>
        <span className={`text-[11px] tabular-nums ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{percent}%</span>
      </div>
      <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
        <div className={`h-full rounded-full ${barTone(status, isLight)}`} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
    </div>
  );
}
